import type { RegistrationSelectedPlan } from "@/types";
import type { BillingInterval } from "@/types/billing";

const PENDING_REGISTRATION_SELECTED_PLAN_KEY =
  "terramatrix.registration.selectedPlan";

const REGISTRATION_TIERS = new Set<string>([
  "free",
  "basic",
  "standard",
  "pro",
] satisfies readonly RegistrationSelectedPlan["tier"][]);

const BILLING_INTERVALS = new Set<string>([
  "monthly",
  "annual",
] satisfies readonly BillingInterval[]);

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const isRegistrationTier = (
  value: string | null,
): value is RegistrationSelectedPlan["tier"] =>
  typeof value === "string" && REGISTRATION_TIERS.has(value);

export const isBillingInterval = (value: string | null): value is BillingInterval =>
  typeof value === "string" && BILLING_INTERVALS.has(value);

export const parsePendingRegistrationSelectedPlan = (
  selectedTier: string | null,
  billingInterval: string | null,
): RegistrationSelectedPlan | undefined => {
  if (!isRegistrationTier(selectedTier)) {
    return undefined;
  }

  if (selectedTier === "free") {
    return {
      tier: selectedTier,
    };
  }

  if (!isBillingInterval(billingInterval)) {
    return undefined;
  }

  return {
    tier: selectedTier,
    billingInterval,
  };
};

export const readPendingRegistrationSelectedPlan = ():
  | RegistrationSelectedPlan
  | undefined => {
  try {
    const rawValue = getSessionStorage()?.getItem(
      PENDING_REGISTRATION_SELECTED_PLAN_KEY,
    );

    if (!rawValue) {
      return undefined;
    }

    const parsedValue: unknown = JSON.parse(rawValue);
    if (parsedValue === null || typeof parsedValue !== "object") {
      return undefined;
    }

    const tier = Reflect.get(parsedValue, "tier");
    const billingIntervalValue = Reflect.get(parsedValue, "billingInterval");
    const selectedTier = typeof tier === "string" ? tier : null;
    const billingInterval =
      typeof billingIntervalValue === "string"
        ? billingIntervalValue
        : null;

    return parsePendingRegistrationSelectedPlan(selectedTier, billingInterval);
  } catch {
    return undefined;
  }
};

export const savePendingRegistrationSelectedPlan = (
  selectedPlan: RegistrationSelectedPlan,
): void => {
  try {
    getSessionStorage()?.setItem(
      PENDING_REGISTRATION_SELECTED_PLAN_KEY,
      JSON.stringify(selectedPlan),
    );
  } catch {
    return;
  }
};

export const clearPendingRegistrationSelectedPlan = (): void => {
  try {
    getSessionStorage()?.removeItem(PENDING_REGISTRATION_SELECTED_PLAN_KEY);
  } catch {
    return;
  }
};

export const appendPendingRegistrationSelectedPlanParams = (
  params: URLSearchParams,
  selectedPlan: RegistrationSelectedPlan | undefined,
): void => {
  if (!selectedPlan) {
    return;
  }

  params.set("selectedTier", selectedPlan.tier);
  if (selectedPlan.tier !== "free") {
    params.set("billingInterval", selectedPlan.billingInterval);
  }
};

export const getPendingRegistrationSelectedPlanPath = (
  pathname: string,
  selectedPlan: RegistrationSelectedPlan | undefined,
): string => {
  if (!selectedPlan) {
    return pathname;
  }

  const params = new URLSearchParams();
  appendPendingRegistrationSelectedPlanParams(params, selectedPlan);
  const query = params.toString();

  return query ? `${pathname}?${query}` : pathname;
};
